import { useQuery, useQueries, useQueryClient, useMutation } from 'react-query';
import APIHelper from './api.helper';
import { BulletinMessages } from '../components/BulletinView/Modal/types';
import {
  BulletinResponse,
  BulletinVariable,
} from '../components/BulletinView/types';

export interface BulletinType {
  code: string;
  name: string;

  cover_picture: string;
  description: string;
  order: number;

  validation_enabled: boolean;

  last_bulletin_date: string;
}

class APIService {
  BASE_URL = 'http://localhost:8000/api/';

  unableToRefreshTokenMsg = 'Unable to refresh token';

  login(credentials: { username: string }) {
    return this.postValue('token/', credentials, false)
      .then(APIHelper.parseJSON<{ access: string }>)
      .then(res => {
        const accessToken = res?.access;
        if (accessToken) {
          APIHelper.setJwtToken(res);
          const decodedToken = APIHelper.decodeToken(accessToken);
          return {
            status: 'success',
            username: credentials?.username,
            hasBulletinPermission:
              decodedToken?.permissions?.can_change_bulletin,
          };
        }
        return {
          status: 'ko',
          username: null,
          hasBulletinPermission: false,
        };
      });
  }

  async isUserConnected() {
    const accessToken = APIHelper.getAccessToken();
    const tokenExpired = APIHelper.isTokenExpired(accessToken);
    const isUserConnected = Boolean(accessToken);
    const notConnectedUser = {
      username: null,
      hasBulletinPermission: null,
    };

    if (isUserConnected && !tokenExpired) {
      return APIHelper.getUserInfoFromToken(accessToken);
    }
    if (isUserConnected && tokenExpired) {
      // Try to refresh token once
      try {
        await this.refreshToken();
        return APIHelper.getUserInfoFromToken(accessToken);
      } catch {
        // we do not throw error here, user will be logout in this case
        return notConnectedUser;
      }
    }

    return notConnectedUser;
  }

  logout() {
    APIHelper.removeJwtToken();
  }

  refreshToken() {
    const url = `${this.BASE_URL}token/refresh/`;
    const data = {
      refresh: APIHelper.getRefreshToken(),
    };

    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then(APIHelper.checkStatus)
      .then(APIHelper.parseJSON<{ access: string }>)
      .then(res => {
        if (res?.access) {
          const token = APIHelper.getJwtToken();
          const newToken = { ...token, access: res.access };
          APIHelper.setJwtToken(newToken);
        } else {
          throw new Error(this.unableToRefreshTokenMsg);
        }
      })
      .catch(() => {
        throw new Error(this.unableToRefreshTokenMsg);
      });
  }

  async postValue(
    ressourceName: string,
    data?: Record<string, unknown>,
    withToken = true,
    method = 'POST',
  ): Promise<Response> {
    const url = `${this.BASE_URL}${ressourceName}`;

    const fetchPost = () =>
      fetch(url, {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(withToken && {
            'X-Access-Token': `Bearer ${APIHelper.getAccessToken()}`,
          }),
        },
        body: JSON.stringify(data),
      });

    return fetchPost()
      .then(APIHelper.checkStatus)
      .catch(async err => {
        const tokenIsExpired = APIHelper.errorIsTokenExpired(err?.message);

        // if error is anything but token expired, raise it
        if (!tokenIsExpired) {
          throw new Error(err);
        }

        await this.refreshToken().catch(error => {
          // if the token is expired, try to refresh it
          // Several cases can occur
          // - 401 => the refresh token itself is expired -> ask for reconnection
          // - other error : the user might not be authorized anymore
          // => logout the user for all the cases

          const refreshTokenIsExpired = error?.message.includes(
            this.unableToRefreshTokenMsg,
          );
          const errorMessage = refreshTokenIsExpired
            ? 'Please login again'
            : error?.message;

          throw new Error(errorMessage);
        });

        return fetchPost().then(APIHelper.checkStatus);
      });
  }

  async getRangeGraphData(
    filters: Record<string, string>[],
    optionsData: Record<string, string>,
    headers: Record<string, string> = {},
  ) {
    const url = `${this.BASE_URL}graph/range?${new URLSearchParams({
      query: JSON.stringify(filters),
      percentile: '20',
      ...optionsData,
    })}`;

    if (headers.Accept === 'text/csv') {
      return fetch(url, { credentials: 'include', headers })
        .then(APIHelper.checkStatus)
        .then(APIHelper.parseText);
    }

    return fetch(url, { credentials: 'include', headers })
      .then(APIHelper.checkStatus)
      .then(APIHelper.parseJSON<{ dates?: string[]; values?: string[] }>)
      .then(res => {
        if (
          !res ||
          !res.dates ||
          res.dates.length < 0 ||
          !res.values ||
          res.values.length < 0
        ) {
          throw new Error('No data available');
        }

        return res;
      });
  }

  patchMapsAsSeen(mapStatus: string) {
    try {
      return this.postValue('maps/', { map_ids: mapStatus }, true, 'PATCH');
    } catch (err) {
      throw new Error(err as string);
    }
  }

  /**
   * Set, update or delete a message for a bulletin system, category, entry or map.
   *
   * @param {*} bulletinType The type of bulletin to send a message for
   * @param {*} bulletinDate The date of the bulletin
   * @param {*} type The type of object to set a message for.
   * @param {*} id The id of the object to set a messge for. For 'code' for system, 'message_code' for category, 'tree_key' for entry and 'id' for map.
   * @param {*} message The string message to set
   * @param {*} edit Whether this is an edit of an existing message.
   * @returns
   */
  postBulletinMessage(
    bulletinType: string,
    bulletinDate: string,
    type: 'system' | 'category' | 'entry' | 'map',
    id: string,
    message: string,
    edit: boolean,
  ) {
    let method = 'POST';
    if (message === '') {
      method = 'DELETE';
    } else if (edit) {
      method = 'PATCH';
    }
    try {
      return this.postValue(
        `message/${bulletinType}/${bulletinDate}/${type}/${id}/`,
        { content: message },
        true,
        method,
      );
    } catch (err) {
      throw new Error(err as string);
    }
  }

  async getBulletinMessages(type: string, date: string) {
    const url = `${this.BASE_URL}bulletins/messages/${type}/${date}`;
    return fetch(url, { credentials: 'include' })
      .then(APIHelper.checkStatus)
      .then(APIHelper.parseJSON<BulletinMessages>);
  }

  async postBulletinValidate(bulletinType: string, bulletinDate: string) {
    try {
      return this.postValue(
        `bulletins/view/${bulletinType}/${bulletinDate}/validate`,
      )
        .then(APIHelper.checkStatus)

        .then(
          APIHelper.parseJSON<
            Array<{
              id: 0;
              key: 'string';

              jira_url: 'string';
              system: 'string';
            }>
          >,
        );
    } catch (err) {
      throw new Error(err as string);
    }
  }

  getAreas(systemId: string) {
    const url = `${this.BASE_URL}areas/${systemId}/`;
    return fetch(url, { credentials: 'include' })
      .then(APIHelper.checkStatus)
      .then(APIHelper.parseJSON);
  }
}

const getBulletinsTypesQuery = () =>
  fetch(`${global.env.API_HOST}bulletins_types/`, {
    credentials: 'include',
  }).then(APIHelper.parseJSON<Array<BulletinType>>);

const getBulletin = (type: string, date: string) =>
  fetch(`${global.env.API_HOST}bulletins/view/${type}/${date}`, {
    credentials: 'include',
  })
    .then(APIHelper.checkStatus)
    .then(APIHelper.parseJSON<BulletinResponse>);

const getBulletinVariable = async (
  type: string,
  entryKey: string,
  date: string,
) => {
  const url = `${global.env.API_HOST}bulletin/${type}/maps/v/${entryKey}/${date}/`;
  return fetch(url, { credentials: 'include' })
    .then(APIHelper.checkStatus)
    .then<BulletinVariable>(APIHelper.parseJSON);
};

export const useBulletinVariableQuery = (
  type: string | undefined,
  entryKey: string | undefined,
  date: string | undefined,
) => {
  const enabled = !!entryKey && !!type && !!date;
  return useQuery({
    queryKey: ['BulletinVariableQuery', type, entryKey, date],
    queryFn: () => {
      if (enabled) {
        return getBulletinVariable(type, entryKey, date);
      }
      throw new Error('No bulletin date, type or entryKey given');
    },
    enabled,
  });
};

const getBulletinReport = (reportId: string) => {
  const url = `${global.env.API_HOST}maps/r/${reportId}`;
  return fetch(url, { credentials: 'include' })
    .then(APIHelper.checkStatus)
    .then(APIHelper.parseJSON);
};

export const useBulletinReportQuery = (type: string | null) =>
  useQuery({
    queryKey: ['BulletinReportQuery', type],
    queryFn: () => {
      if (type != null) {
        return getBulletinReport(type);
      }
      throw new Error('No bulletin type given');
    },
    enabled: type !== null,
  });

export const useBulletinQuery = (
  type: string | undefined,
  date: string | undefined,
) =>
  useQuery<BulletinResponse, Response>({
    queryKey: ['BulletinQuery', type, date],
    queryFn: () => {
      if (type && date) {
        return getBulletin(type, date);
      }
      throw new Error('No bulletin date or type given');
    },
    enabled: type !== undefined,
    retry: false,
  });

const getNavLinksQuery = async () => {
  const bulletinLinks = await getBulletinsTypesQuery();
  return {
    bulletinLinks: bulletinLinks.map(item => ({
      code: item.code,
      name: item.name,
      to: `/bulletin/${item.code}`,
      image: item.cover_picture,
      description: item.description,
    })),
    OMI: {
      name: 'Copernicus Marine Ocean Monitoring indicators',
      to: '/omi',
      image: 'homeImages/moniqua_omi.png',
      description:
        'View a selection of integrated time-dependent indicators, including the Copernicus Marine Service Monitoring Indicators',
    },
    viewLinks: [
      {
        name: 'LON/LAT maps',
        to: '/map-view',
        image: 'homeImages/moniqua_lonlat_maps.png',
        description:
          'Interactively access the complete database of static bulletin images and complementary graphics',
      },

      {
        name: 'TIME SERIES',
        to: '/range',
        image: 'homeImages/moniqua_timeserie.png',
        description:
          'Interactive access to the database of statistics comparing the operational model outputs with observations',
      },
    ],
    expertLinks: [
      {
        name: 'DEPTH VS TIME',
        to: '/tz-view',
        image: 'homeImages/moniqua_tz-view.png',
        description:
          'Visualise the depth vs time evolution of different scores with respect with insitu temperature and salinity observations',
      },
      {
        name: 'VERTICAL PROFILE',
        to: '/in-situ',
        image: 'homeImages/moniqua_vprof.png',
        description:
          'Visualise the average difference profiles to in situ observations of temperature and salinity as a fonction of depth',
      },
    ],
  };
};

export const useNavLinksQuery = () =>
  useQuery('NavLinksQuery', getNavLinksQuery);

const getOMIExistingFiltersQuery = () =>
  fetch(`${global.env.API_HOST}omi/filters`, { credentials: 'include' }).then(
    APIHelper.parseJSON,
  );

export const getOMIGraphDataQuery = (filters: Record<string, unknown>) =>
  fetch(
    `${global.env.API_HOST}graph/omi?${new URLSearchParams({
      query: JSON.stringify(filters),
    })}`,
    { credentials: 'include' },
  ).then(APIHelper.parseJSON);

export const getOMICSV = (filters: Record<string, unknown>) =>
  fetch(
    `${global.env.API_HOST}graph/omi?${new URLSearchParams({
      query: JSON.stringify(filters),
      csv: 'true',
    })}`,
    { credentials: 'include' },
  )
    .then(APIHelper.checkStatus)
    .then(APIHelper.parseText);

export const getBulletinDateListQuery = (type: string) =>
  fetch(`${global.env.API_HOST}bulletins/list/${type}`, {
    credentials: 'include',
  })
    .then(APIHelper.parseJSON<Array<{ date: string }>>)
    .then(list => list.map(({ date }) => new Date(date)));

export const useOmiExistingFiltersQuery = () =>
  useQuery('omiExistingFiltersQuery', getOMIExistingFiltersQuery);

export const useOmiGraphDataQuery = (filters: Record<string, unknown>) =>
  useQuery(['omiGraphDataQuery', filters], () => getOMIGraphDataQuery(filters));

export const useBulletinDateListQuery = (type: string) =>
  useQuery(['bulletinDateListQuery'], () => getBulletinDateListQuery(type));

export const useBulletinsTypes = () =>
  useQuery(['bulletinsTypes'], getBulletinsTypesQuery);

const getMapExistingFilters = async (date: string | null) => {
  let url = null;
  if (date === null) {
    url = `${global.env.API_HOST}lonlat/filters/`;
  } else {
    url = `${global.env.API_HOST}lonlat/filters/${date}/`;
  }
  return fetch(url, { credentials: 'include' })
    .then(APIHelper.checkStatus)
    .then(APIHelper.parseJSON);
};
export const useMapExistingFiltersQuery = (date: string | null = null) =>
  useQuery(['mapExistingFilter', date], () => getMapExistingFilters(date));

export const useMapExistingFiltersQueries = (
  filters: Array<{ date: string | null }>,
) =>
  useQueries(
    filters.map(({ date = null }) => ({
      queryKey: ['mapExistingFilters', date],
      queryFn: () => getMapExistingFilters(date),
    })),
  );

const getPicture = (pictureAttributes: Record<string, unknown>) => {
  // extract all param except system
  const { system, ...queryParams } = pictureAttributes;
  const url = `${global.env.API_HOST}picture/${system}?${new URLSearchParams({
    query: JSON.stringify(queryParams),
  })}`;
  return fetch(url, { credentials: 'include' })
    .then(APIHelper.checkStatus)
    .then(APIHelper.parseJSON);
};

export const usePictureQueries = (filters: Array<Record<string, unknown>>) =>
  useQueries(
    filters.map(filter => ({
      queryKey: ['mapPictures', filter],
      queryFn: () => getPicture(filter),
      enabled: filter !== undefined,
    })),
  );

export const getRangeGraphData = (
  filters: Array<Record<string, unknown>>,
  optionsData = { percentile: '20' },
  headers: Record<string, string> = {},
) => {
  const url = `${global.env.API_HOST}graph/range?${new URLSearchParams({
    query: JSON.stringify(filters),
    percentile: optionsData.percentile,
  })}`;

  if (headers.Accept === 'text/csv') {
    return fetch(url, { credentials: 'include', headers })
      .then(APIHelper.checkStatus)
      .then(APIHelper.parseText);
  }

  return fetch(url, { credentials: 'include' })
    .then(APIHelper.checkStatus)
    .then(APIHelper.parseJSON<{ date?: string; values?: string[] }>)
    .then(res => {
      if (
        !res ||
        !res.date ||
        res.date.length < 0 ||
        !res.values ||
        res.values.length < 0
      ) {
        return {};
      }

      const { date, ...rest } = res;

      return { ...rest, dates: date };
    });
};

export const useRangeGraphDataQuery = (
  filters: Array<Record<string, unknown>>,
  optionsData = { percentile: '20' },
) =>
  useQuery<string | { date?: string; values?: string[] }>(
    ['rangeGraphDataListQuery', ...filters],
    () => getRangeGraphData(filters, optionsData),
    { enabled: filters.length > 0 },
  );

export const getInSituGraphData = (
  params: Array<Record<string, unknown>>,
  headers: Record<string, string> = {},
) => {
  const query = JSON.stringify(params);
  if (query === '[{}]') {
    return null;
  }
  const url = `${global.env.API_HOST}graph/in-situ?${new URLSearchParams({
    query,
  })}`;

  if (headers.Accept === 'text/csv') {
    return fetch(url, { credentials: 'include', headers })
      .then(APIHelper.checkStatus)
      .then(APIHelper.parseText);
  }

  return fetch(url, { credentials: 'include' })
    .then(APIHelper.checkStatus)
    .then(APIHelper.parseJSON);
};

export const useInSituGraphDataQuery = (
  filters: Array<Record<string, unknown>>,
) =>
  useQuery(
    ['inSituGraphDataListQuery', ...filters],
    () => getInSituGraphData(filters),
    { enabled: filters.length > 0 },
  );

export const get3DViewGraphData = (params: Array<Record<string, unknown>>) => {
  const query = JSON.stringify(params);
  if (query === '[{}]') {
    return null;
  }
  const url = `${global.env.API_HOST}graph/3d?${new URLSearchParams({
    query,
  })}`;

  return fetch(url, { credentials: 'include' })
    .then(APIHelper.checkStatus)
    .then(APIHelper.parseJSON)
    .then(
      res =>
        // if (!res.depths.length || !res.values.length) {
        //   throw new Error('No data available');
        // }

        res,
    );
};

const apiService = new APIService();

export const use3DGraphDataQuery = (filters: Array<Record<string, unknown>>) =>
  useQuery(
    ['i3DGraphDataListQuery', filters],
    () => get3DViewGraphData(filters),
    { enabled: filters?.length > 0 },
  );

export const useGetBulletinMessage = (
  bulletinType: string | undefined,
  bulletinDate: string | undefined,
  enabled: boolean,
) =>
  useQuery(
    ['BulletinMessages', bulletinType, bulletinDate],
    async () => {
      if (bulletinType && bulletinDate) {
        return apiService
          .getBulletinMessages(bulletinType, bulletinDate)
          .then(m => {
            m.systems.sort((a, b) => a.order - b.order);
            m.systems.forEach(system => {
              system.categories.sort((a, b) => a.order - b.order);
            });
            return m;
          });
      }
      throw new Error('No bulletin date or type given');
    },
    {
      enabled:
        enabled && bulletinType !== undefined && bulletinDate !== undefined,
    },
  );

export const useValidateBulletinMutation = (
  bulletinType: string | undefined,
  bulletinDate: string | undefined,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (bulletinType && bulletinDate) {
        return apiService.postBulletinValidate(bulletinType, bulletinDate);
      }
      throw new Error('No bulletin date or type given');
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['BulletinQuery', bulletinType, bulletinDate],
      }),
  });
};

export const usePostBulletinMessageMutation = (
  bulletinType: string | undefined,
  bulletinDate: string | undefined,
  type: 'system' | 'category' | 'entry' | 'map',
  id: string,
  edit: boolean,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (message: string) => {
      if (bulletinType && bulletinDate) {
        return apiService.postBulletinMessage(
          bulletinType,
          bulletinDate,
          type,
          id,
          message,
          edit,
        );
      }
      throw new Error('No bulletin date or type given');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['BulletinMessages', bulletinType, bulletinDate],
      });
      queryClient.invalidateQueries({
        queryKey: ['BulletinQuery', bulletinType, bulletinDate],
      });
    },
  });
};

export default apiService;
